import type {
  FenceView,
  RosterDriver,
} from "@tdelivr/api-client/generated/experience/dispatch-console-bff.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

/**
 * What the live map does to the one answer EXP-008 gives it, away from any rendering.
 *
 * The same split as `demand-queue.ts` and `triage.ts`: the decisions can be stated and tested
 * without a browser, and a component that makes them inline can only be checked by rendering it.
 * On this screen the split earns more than usual, because design rule 5 is a rule about pixels —
 * which glyph, which colour, which outline, which halo — and a rule about pixels that lives in
 * JSX is a rule nothing can assert. Here it is a table, and `tests/live-map.test.ts` reads it.
 *
 * **Half of this file used to be a join and is now gone.** The screen read seven services and put
 * their rows together here: `joinRoster` on `driverId`, `rosterOrder`, `rosterCounts`,
 * `onlineDrivers`, `driverPresence`, `demandOrder`, `serviceAreaRing` and `geofencePoints`. R-09
 * measured why that was the wrong place for it — twelve read scopes in one browser session, seven
 * snapshots taken at seven instants, and a GeoJSON axis swap that draws a service area in the
 * Indian Ocean without anything looking broken — and
 * [ADR 0020](../../../docs/adr/0020-the-experience-tier-is-shaped-like-a-service.md) built the
 * aggregate that does it instead. Every one of those eight functions now lives in
 * `services/experience/dispatch-console-bff/src/domain/live-map.ts`, with its reasoning, and the
 * screen takes `drivers`, `demand`, `serviceAreas` and `geofences` as given. R-14 records the move.
 *
 * What is left is what only a screen can decide: which pixels a published state is drawn as, where
 * a coordinate lands on the surface, and how a clock is spelled.
 *
 * Two things this module still refuses to do, both for the same reason.
 *
 * **It never turns a missing field into a plausible one.** A driver's vehicle class is not
 * published anywhere a driver can be joined to, so `driverGlyph` answers the design's neutral
 * `person` rather than guessing between `local_taxi` and `two_wheeler`. The two real glyphs are
 * still here and still selected the moment a vehicle class arrives.
 *
 * **It never places a marker it has no coordinate for.** A dispatch whose pickup the aggregate
 * could not read carries `origin: null` and a reason, and a trip still publishes no destination and
 * no route geometry, so those pins and lines are absent rather than scattered. R-09 in
 * `docs/design/DESIGN_RECONCILIATION.md` is the measurement behind that.
 */

/* -------------------------------------------------------------------------------------------- */
/* Design rule 5: what a marker looks like                                                        */
/* -------------------------------------------------------------------------------------------- */

/**
 * The four ways a driver can be on this map, in the design's own vocabulary.
 *
 * `05 Dispatch Live Map.dc.html` calls them `job`, `available`, `stale` and `offline`, and its
 * `dState()` maps each to a state class and a word. Both are reproduced below rather than
 * paraphrased.
 *
 * Taken from the aggregate's own type rather than restated, because the four words are now on the
 * wire: `RosterDriver.presence` is the same four, and a fifth appearing there should break this
 * file rather than fall through a `default`.
 */
export type DriverPresence = RosterDriver["presence"];

/**
 * What the belief that a driver is working rests on, as EXP-008 publishes it.
 *
 * `trip` is evidence — TRP-02 answered a running trip. `undispatchable` is an inference: DRV-05 is
 * holding the driver out of supply and its `reasonCode` is a free-form string, so a courier
 * carrying a parcel is indistinguishable from a driver on a break. The screen used to have one
 * flag and no way to say which it was looking at; this is the field that changed that, and
 * `markerKeyFor` below is where it becomes something an operator can see.
 */
export type BusyEvidence = NonNullable<RosterDriver["busyEvidence"]>;

/** How a marker is outlined, which is half of how its state is told apart. */
export type MarkerOutline = "solid" | "dashed";

/**
 * What sits behind the marker.
 *
 * `none` for an available driver, `glow` for the design rule's blue halo on a job, and `accuracy`
 * for the dashed circle a stale location is drawn inside — sized from the fix's own
 * `horizontalAccuracyMetres` rather than from a constant, because the circle is a claim about how
 * wrong the position may be.
 */
export type MarkerHalo = "none" | "glow" | "accuracy";

export interface MarkerLook {
  /** The fill, or `null` when the marker is drawn on the surface colour instead. */
  readonly fill: string | null;
  /** The glyph and border colour. */
  readonly ink: string;
  readonly outline: MarkerOutline;
  readonly halo: MarkerHalo;
  /** The state class the badge beside the name borrows its glyph and hue from. */
  readonly classKey: StateClassKey;
  /** The word on that badge, exactly as the design writes it. */
  readonly word: string;
  /** False for `offline`, which design rule 5 says is drawn as no marker at all. */
  readonly drawn: boolean;
}

/**
 * The looks a driver marker can have: design rule 5's four, and `job` split by its evidence.
 *
 * The fifth is the one Q-12 asked for and rule 5 does not define. See `markerKeyFor` for what it
 * is drawn as and why that shape rather than another.
 */
export type MarkerKey = DriverPresence | "job_inferred";

/**
 * Design rule 5's driver colours, as the design file writes them.
 *
 * The three values CLAUDE.md quotes and the three the design uses agree: `#067647` available,
 * `#026AA2` on a job, and grey for a stale location. They agree on the greys too, but only in the
 * light theme — the rule names `#475467` and the design writes `var(--text2)`, which *is* `#475467`
 * under the light palette and `#CECFD2` under the dark one. The design's reading is taken, because
 * a fixed `#475467` on the dark map surface (`--mapBase` is `#111827`) would be a marker nobody
 * could see. Recorded in R-09.
 *
 * The other two stay literal in both themes, exactly as the design writes them: `--s-success-fg`
 * and `--s-info-fg` flip to pale mint and pale sky in the dark palette, and a pale fill under the
 * white glyph these markers carry would be unreadable. A marker sits on the map, not on a card, so
 * it does not follow the card palette.
 *
 * Every one of the five is told apart by more than its colour, which is the rule that has no
 * exceptions: a filled disc, a filled disc inside a halo, the same disc with its ring dashed, a
 * dashed ring inside an accuracy circle, and nothing at all.
 */
export const DRIVER_MARKERS: Readonly<Record<MarkerKey, MarkerLook>> = {
  available: {
    fill: "#067647",
    ink: "#FFFFFF",
    outline: "solid",
    halo: "none",
    classKey: "done",
    word: "available",
    drawn: true,
  },
  job: {
    fill: "#026AA2",
    ink: "#FFFFFF",
    outline: "solid",
    halo: "glow",
    classKey: "active",
    word: "on a job",
    drawn: true,
  },
  /*
   * A driver the platform infers is working, drawn in the design's own vocabulary for "not
   * confirmed".
   *
   * Same fill, same glyph, same size and same halo as a confirmed job, because the operational
   * instruction is identical: do not send this driver a second job. What differs is the ring. The
   * design draws every marker's ring as `3px solid var(--surface)`; this one is dashed, which is
   * the idiom the design itself uses for a state it is not sure of — the stale marker's dashed
   * ring, and the token system's `unknown` class, whose recorded rationale is "Dashed and grey:
   * borrows neither success nor failure, because neither is true yet". No new colour and no new
   * word: the badge keeps the design's `on a job` and takes the `unknown` class's glyph and hue.
   *
   * Q-25 records that rule 5 names four markers and this is a fifth, and what it costs.
   */
  job_inferred: {
    fill: "#026AA2",
    ink: "#FFFFFF",
    outline: "dashed",
    halo: "glow",
    classKey: "unknown",
    word: "on a job",
    drawn: true,
  },
  stale: {
    fill: null,
    ink: "var(--text2)",
    outline: "dashed",
    halo: "accuracy",
    classKey: "unknown",
    word: "stale location",
    drawn: true,
  },
  offline: {
    fill: null,
    ink: "var(--text2)",
    outline: "dashed",
    halo: "none",
    classKey: "closed",
    word: "offline",
    drawn: false,
  },
};

/**
 * Which look one driver gets, given what the aggregate says the belief rests on.
 *
 * This is the one place where R-09's over-claim stops being silent. The claim itself is unchanged
 * and still deliberate — a driver DRV-05 is withholding from supply, on no trip this console can
 * see, is drawn as working, because two of the four jobs the design draws are deliveries and DEL-06
 * publishes no listing of them, so withheld-from-supply is the only evidence on the wire that those
 * couriers are carrying anything. What changes is that an operator can now tell the two apart: a
 * solid ring is a trip TRP-02 named, a dashed ring is an inference from a free-form `reasonCode`
 * that a driver on a break would also produce.
 *
 * It is drawn rather than only reported because the two lead to different actions. A dispatcher who
 * must reach a courier mid-delivery calls them; one looking at a driver who may simply be on a
 * break asks DRV-05 why. Publishing the evidence and then drawing both the same would have moved
 * the over-claim one layer down rather than ended it.
 *
 * `busyEvidence` is absent for every presence but `job`, and absent on a `job` only if the
 * aggregate stopped publishing it — which is treated as the confirmed look, because that is the
 * design's own marker and the one the screen drew before this field existed.
 */
export function markerKeyFor(presence: DriverPresence, busyEvidence?: BusyEvidence): MarkerKey {
  if (presence !== "job") return presence;
  return busyEvidence === "undispatchable" ? "job_inferred" : "job";
}

/**
 * The two demand pins, as the design draws them.
 *
 * Violet waiting, red failing, which is the design's `#7C3AED` and `#D92D20` and the two keys its
 * legend already carries. Reached as tokens in the stylesheet rather than as literals here —
 * `--s-attention-solid` and `--s-danger-solid` are those exact values — so this table carries the
 * class and the word rather than the colour, and the rule that a state is never told by colour
 * alone is kept by the tag above every pin: a failing dispatch reads `No driver` where a waiting
 * one reads a countdown.
 *
 * Both halves of `failing` come from DSP-05 and are the design's own rule, restated here from the
 * queue row that already applies it: a failed search, or one inside two minutes of its deadline.
 */
export interface DemandPinLook {
  /** The stylesheet modifier, which is also the legend key the design gives this pin. */
  readonly key: "demand-waiting" | "demand-failing";
  /** The legend entry it answers to, so a key that stopped matching its pin is a test failure. */
  readonly legend: string;
}

export const DEMAND_PINS: Readonly<Record<"waiting" | "failing", DemandPinLook>> = {
  waiting: { key: "demand-waiting", legend: "Demand waiting" },
  failing: { key: "demand-failing", legend: "Demand failing" },
};

/** The word above a pin with nothing left to count, exactly as the design writes it. */
export const DEMAND_EXHAUSTED = "No driver";

/**
 * Design rule 5's route colours, and the screen draws one of the four now.
 *
 * This docblock used to say nothing could draw a route at all, and that the blocker was a geometry
 * nothing published. **Measured against the design rather than the rule, that was the wrong
 * blocker.** The design draws a job's route as a single rotated rule from one point to another with
 * a pin at the far end — two coordinates and no shape — and every `polyline` in the whole design
 * bundle is the Material Symbols glyph name on the Geofences navigation item. There is no
 * road-following route on this platform to draw.
 *
 * So what was missing was the second coordinate, and it took two hops that were both already
 * published: TRP-01's trips listing names a trip's request, and MOB-03's request holds the
 * destination. EXP-008 makes that join and publishes `tripDestination` per driver; `jobLine` below
 * turns the pair into the design's own geometry.
 *
 * `in_trip` is the one that is drawn, because every job on this board is a trip in progress — the
 * roster's `tripId` comes from TRP-02's **active** markers. The other three remain unreachable and
 * remain here, for the reason they always did: the rule is the platform's rather than this screen's.
 * A completed or cancelled trip is not on an active listing, and nothing publishes a disputed one.
 *
 * Every entry pairs a colour with a stroke, because a route told apart by colour alone would break
 * the rule that has no exceptions. `dashed` and `dotted` are the design file's own two strokes.
 */
export type RouteKind = "in_trip" | "completed" | "cancelled" | "needs_person";

export interface RouteLook {
  readonly colour: string;
  readonly stroke: "solid" | "dashed" | "dotted";
  readonly word: string;
}

export const ROUTE_LOOKS: Readonly<Record<RouteKind, RouteLook>> = {
  in_trip: { colour: "#026AA2", stroke: "solid", word: "in trip" },
  completed: { colour: "#067647", stroke: "solid", word: "completed" },
  cancelled: { colour: "#475467", stroke: "dashed", word: "cancelled" },
  needs_person: { colour: "#7C3AED", stroke: "dotted", word: "needs a person" },
};

/** The surface's measured size in pixels, which the line's geometry cannot be computed without. */
export interface SurfaceSize {
  readonly width: number;
  readonly height: number;
}

/** One job line, as the design's own arithmetic produces it. */
export interface JobLine {
  /** Percent of the surface, from the left and the top: the line's origin and its pivot. */
  readonly left: number;
  readonly top: number;
  /** Pixels, because the design sets a width in pixels and rotates about the start. */
  readonly length: number;
  /** Degrees clockwise from east, as `atan2` gives it in screen space. */
  readonly angle: number;
}

/**
 * A rotated rule from one placement to another, copied from the design's own four lines.
 *
 * The design writes it as:
 *
 *     const dx=(x2-x1)*bw/100, dy=(y2-y1)*bh/100;
 *     const len=Math.hypot(dx,dy); const ang=Math.atan2(dy,dx)*180/Math.PI;
 *     {left:x1+'%', top:y1+'%', width:len, height:0, borderTop:…,
 *      transform:'rotate('+ang+'deg)', transformOrigin:'0 0'}
 *
 * **The measured size is not an implementation detail that could be skipped.** The two endpoints
 * are percentages of a box that is wider than it is tall, so a line between them is not at the
 * angle those percentages suggest: converting to pixels first is what makes the rule land on the
 * pin instead of near it. A version of this that rotated by `atan2(y2-y1, x2-x1)` on the
 * percentages would look plausible on a square surface and be visibly wrong on this one.
 *
 * Returned as data rather than as a style object so it can be asserted without a DOM.
 */
export function jobLine(from: Placement, to: Placement, surface: SurfaceSize): JobLine {
  const dx = ((to.left - from.left) * surface.width) / 100;
  const dy = ((to.top - from.top) * surface.height) / 100;
  return {
    left: from.left,
    top: from.top,
    length: Math.hypot(dx, dy),
    angle: (Math.atan2(dy, dx) * 180) / Math.PI,
  };
}

/**
 * The glyph a driver marker carries.
 *
 * Design rule 5 names two: `local_taxi` for a car and `two_wheeler` for a motorcycle. Both are
 * here. Neither can be chosen today, and the reason is not that the field is hard to find — it is
 * that no operation joins a driver to a vehicle. DRV-04 publishes `vehicleClass` on a vehicle and
 * answers only `GET /v1/vehicles/{vehicleId}`: no listing, and no `driverId` on the record, so
 * there is nothing to look a driver's vehicle up by and nothing to enumerate. DRV-01's profile has
 * no vehicle either.
 *
 * So the fallback is `person`, which is the design's own glyph for a driver — it is what its
 * selection card and its roster rows draw — rather than a pictogram chosen here. The same move
 * `navigation.ts` makes for a page the shell's icon map does not name.
 *
 * `vehicleClass` is a free-form `string` in DRV-04, not an enum, so the match is on what the word
 * contains rather than on a value set nobody published.
 */
export function driverGlyph(vehicleClass?: string): string {
  if (vehicleClass === undefined) return "person";
  const word = vehicleClass.toLowerCase();
  if (word.includes("motorcycle") || word.includes("moto") || word.includes("bike")) {
    return "two_wheeler";
  }
  if (word.includes("car") || word.includes("sedan") || word.includes("taxi")) return "local_taxi";
  return "person";
}

/* -------------------------------------------------------------------------------------------- */
/* Putting a coordinate on the surface                                                            */
/* -------------------------------------------------------------------------------------------- */

export interface Coordinate {
  readonly latitude: number;
  readonly longitude: number;
}

/** Where a coordinate lands on the surface, as the percentages the design positions markers with. */
export interface Placement {
  readonly left: number;
  readonly top: number;
}

export interface Projection {
  place(point: Coordinate): Placement;
  /** How many percent of the surface's width one metre covers, for the accuracy circle. */
  readonly percentPerMetre: number;
}

/**
 * Metres per degree of latitude, near enough for a city.
 *
 * The map covers one operating area — a few tens of kilometres at most — so a sphere and an
 * ellipsoid differ by less than the width of a marker. This constant exists so the accuracy circle
 * has a real radius rather than a chosen one: a `±48 m` fix draws a circle 48 metres across on the
 * same scale as the drivers inside it.
 */
const METRES_PER_DEGREE_LATITUDE = 111_320;

/**
 * Fit every point on the surface, with the design's own margin around them.
 *
 * An equirectangular fit, which is what a bounded city view wants and what needs no library and no
 * network: longitude is squeezed by the cosine of the mid latitude so a square block is drawn
 * square, and the box is then padded so no marker sits on the edge.
 *
 * A box with no extent — one driver, or several at one address — would divide by zero and put
 * everything at the same corner, so a minimum span is imposed. It is expressed in degrees rather
 * than pixels because it is a statement about ground, not about the screen: roughly a kilometre,
 * which is the smallest view in which two city markers are still distinguishable.
 */
export function projectionFor(
  points: readonly Coordinate[],
  options: { readonly paddingPercent?: number } = {},
): Projection {
  const padding = options.paddingPercent ?? 10;
  const usable = 100 - 2 * padding;

  const latitudes = points.map((point) => point.latitude);
  const longitudes = points.map((point) => point.longitude);
  const centreLatitude =
    latitudes.length === 0 ? 0 : (Math.min(...latitudes) + Math.max(...latitudes)) / 2;
  const squeeze = Math.max(0.1, Math.cos((centreLatitude * Math.PI) / 180));

  const MINIMUM_SPAN_DEGREES = 0.01;
  const span = (values: readonly number[], divisor: number): { min: number; size: number } => {
    if (values.length === 0) return { min: -MINIMUM_SPAN_DEGREES / 2, size: MINIMUM_SPAN_DEGREES };
    const low = Math.min(...values);
    const high = Math.max(...values);
    const size = Math.max(high - low, MINIMUM_SPAN_DEGREES / divisor);
    // Re-centre on the points, so a widened span grows evenly rather than off one edge.
    return { min: (low + high) / 2 - size / 2, size };
  };

  const vertical = span(latitudes, 1);
  const horizontal = span(longitudes, squeeze);

  return {
    place(point: Coordinate): Placement {
      return {
        left: padding + ((point.longitude - horizontal.min) / horizontal.size) * usable,
        // Latitude grows north and `top` grows south, so the axis is flipped rather than scaled.
        top: padding + ((vertical.min + vertical.size - point.latitude) / vertical.size) * usable,
      };
    },
    percentPerMetre: usable / (horizontal.size * squeeze * METRES_PER_DEGREE_LATITUDE),
  };
}

/**
 * Every vertex of a geofence, so the projection can be fitted around the fences as well.
 *
 * All this does is collect points for the fit, which is a question about the surface and therefore
 * the screen's. `geofencePoints` before it did the same job against GEO-05's own payload; what
 * moved to the aggregate is the reading of the two shapes a fence can be and the axis order their
 * coordinates come in, which is the part that can be wrong without looking wrong.
 */
export function fencePoints(fence: FenceView): Coordinate[] {
  return fence.shape.kind === "circle" ? [fence.shape.centre] : [...fence.shape.vertices];
}

/* -------------------------------------------------------------------------------------------- */
/* Words and clocks                                                                               */
/* -------------------------------------------------------------------------------------------- */

/**
 * The countdown, in the design's own format.
 *
 * `m:ss`, floored at zero, because a negative clock on a deadline that has passed reads as a bug
 * rather than as news. The design's `fmtClock` does the same.
 */
export function formatClock(millisecondsLeft: number): string {
  const seconds = Math.max(0, Math.round(millisecondsLeft / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/**
 * How old a fix is, in the two units the design uses.
 *
 * Seconds below a minute and whole minutes above it — `3s`, `14m` — which is what the roster's
 * right-hand column and the stale marker's tag both show.
 */
export function formatAge(ageMilliseconds: number): string {
  const seconds = Math.max(0, Math.round(ageMilliseconds / 1000));
  return seconds < 60 ? `${seconds}s` : `${Math.round(seconds / 60)}m`;
}

/** The same age spelled out, as the selection card writes it: `14 min ago`, `42 s ago`. */
export function formatLastFix(ageMilliseconds: number): string {
  const seconds = Math.max(0, Math.round(ageMilliseconds / 1000));
  return seconds < 60 ? `${seconds} s ago` : `${Math.round(seconds / 60)} min ago`;
}
